import apiFetch from '@wordpress/api-fetch'
import {
    createReduxStore,
    dispatch,
    register,
    StoreDescriptor,
} from '@wordpress/data'
import { addQueryArgs } from '@wordpress/url'
import { IFormData } from '../../frontend/screens/BookingForm/types'
import { IField } from '../../frontend/components/Form/types'
import { IPaymentMethod } from '../../frontend/components/PaymentSelector/types'
import { wbkDecodeString } from '../../frontend/lib/stringSanitizer'

interface Booking {
    id: string | number
    [key: string]: any
}

interface Preset {
    user?: string
    [key: string]: any
}

interface FormData {
    services: any[]
    offset: number | null
    date: string | null
    booking: string | number | null
    time: string | null
    [key: string]: any
    category?: number
}

interface DynamicAttributes {
    timeSlots: any
    [key: string]: any
}

interface State {
    userFutureBookings: Booking[] | null
    userPastBookings: Booking[] | null
    preset: Preset
    formData: FormData
    dynamicAttributes: DynamicAttributes
    // serviceUnavailableDates: unknown
    // serviceTimeslots: unknown
    // bookingFormFields: IField[]
    // paymentMethods: IPaymentMethod[]
    // bookingData: unknown
    bookingAmounts: {
        total: number
        subtotal: number
        discount: number
        tax: number
        items: any[]
    }
    loading: {
        preset: boolean
        timeSlots: boolean
        serviceAvailability: { [serviceId: number]: boolean }
        serviceTimeslots: { [serviceId: number]: boolean }
        bookingFields: boolean
        paymentMethods: boolean
        bookingAmounts: boolean
        createBooking: boolean
    }
    bookingDetails: { [key: string]: any }
    [key: string]: any
}

// Helper function to get current language
const getCurrentLanguage = (): string => {
    const locale =
        (window as any)?.wp?.i18n?.getLocale?.() ||
        document.documentElement.lang ||
        'en_US'

    // Extract language code (first part before underscore or dash)
    // Handle cases: 'pl_PL' -> 'pl', 'en-US' -> 'en', 'fr' -> 'fr'
    if (locale.includes('_')) {
        return locale.split('_')[0]
    } else if (locale.includes('-')) {
        return locale.split('-')[0]
    } else {
        return locale || 'en'
    }
}

// Helper function to add language to query args
const addLangToQueryArgs = (path: string, args: Record<string, any> = {}) => {
    return addQueryArgs(path, {
        ...args,
        lang: getCurrentLanguage(),
    })
}

const DEFAULT_STATE: State = {
    userFutureBookings: null,
    userPastBookings: null,
    preset: {},
    formData: {
        services: [],
        offset: null,
        date: null,
        booking: null,
        time: null,
    },
    dynamicAttributes: {
        timeSlots: null,
    },
    serviceUnavailableDates: {},
    serviceTimeslots: {},
    bookingFormFields: [],
    paymentMethods: [],
    bookingData: null,
    bookingAmounts: {
        total: 0,
        subtotal: 0,
        discount: 0,
        tax: 0,
        items: [],
    },
    loading: {
        preset: true,
        timeSlots: false,
        serviceAvailability: false,
        serviceTimeslots: {},
        bookingFields: false,
        paymentMethods: false,
        bookingAmounts: false,
        createBooking: false,
    },
    bookingDetails: {},
}

const actions = {
    setUserFutureBookings(bookings: Booking[]) {
        return { type: 'SET_USER_FUTURE_BOOKING', bookings }
    },
    setUserPastBookings(bookings: Booking[]) {
        return { type: 'SET_USER_PAST_BOOKING', bookings }
    },
    setPreset(preset: Preset) {
        return { type: 'SET_PRESET', preset }
    },
    setPresetLoading(loading: boolean) {
        return { type: 'SET_PRESET_LOADING', loading }
    },
    setUserName(userName: string) {
        return { type: 'SET_USER_NAME', userName }
    },
    setFormData(key: keyof FormData, value: any) {
        return { type: 'SET_FORM_DATA', key, value }
    },
    setDynamicAttribute(key: keyof DynamicAttributes, value: any) {
        return { type: 'SET_DYNAMIC_ATTRIBUTE', key, value }
    },
    setLoading(
        key: keyof State['loading'],
        value: any,
        subkey?: string | number
    ) {
        return { type: 'SET_LOADING', key, value, subkey }
    },
    fetchTimeSlots:
        () =>
        async ({ select, dispatch }: any) => {
            const queryString = new URLSearchParams(
                select.getFormData()
            ).toString()
            const response = await apiFetch({
                path: `/wbk/v2/get-time-slots/?${queryString}`,
            })
            dispatch.setDynamicAttribute(
                'timeSlots',
                (response as any).timeslots
            )
        },
    fetchServiceAvailability: (
        service_id: Number,
        start_date: string,
        end_date: string
    ) => {
        return async ({ select, dispatch }: any) => {
            try {
                dispatch.setLoading('serviceAvailability', true, service_id)

                const response = await apiFetch({
                    path: addQueryArgs(
                        `webba-booking/v1/get-service-availability/`,
                        {
                            service_id,
                            start_date,
                            end_date,
                        }
                    ),
                    method: 'GET',
                })

                dispatch({
                    type: 'SET_SERVICE_AVAILABILITY',
                    data: response,
                })
            } finally {
                dispatch.setLoading('serviceAvailability', false, service_id)
            }
        }
    },
    fetchServiceTimeslots: (
        date: string,
        serviceId: number,
        offset: number
    ) => {
        return async ({ select, dispatch }: any) => {
            dispatch.setLoading('serviceTimeslots', true, serviceId)
            try {
                const response = (await apiFetch({
                    path: addQueryArgs(
                        `/webba-booking/v1/get-service-time-slots/`,
                        {
                            date,
                            service_id: serviceId,
                            offset,
                        }
                    ),
                    method: 'GET',
                })) as { time_slots: any }

                dispatch({
                    type: 'SET_SERVICE_TIMESLOTS',
                    date,
                    serviceId,
                    offset,
                    timeslots: response.time_slots,
                })
            } finally {
                dispatch.setLoading('serviceTimeslots', false, serviceId)
            }
        }
    },
    fetchBookingFields: (services: number[]) => {
        return async ({ dispatch }: any) => {
            if (services.length === 0) return

            const response = await apiFetch({
                path: addQueryArgs(`webba-booking/v1/get-form-fields`, {
                    ids: services,
                }),
            })

            dispatch({
                type: 'SET_BOOKING_FORM_FIELDS',
                data: response,
            })
        }
    },
    fetchPaymentMethods: (services: number[]) => {
        return async ({ dispatch }: any) => {
            try {
                const response = await apiFetch({
                    path: addQueryArgs(`webba-booking/v1/get-payment-methods`, {
                        ids: services,
                    }),
                })

                dispatch({
                    type: 'SET_PAYMENT_METHODS',
                    data: response,
                })
            } catch (e) {
                dispatch({
                    type: 'SET_PAYMENT_METHODS',
                    data: {
                        data: {
                            payment_methods: [],
                        },
                    },
                })
            }
        }
    },
    fetchBookingAmounts: (formData: IFormData) => {
        return async ({ dispatch }: any) => {
            const response = await apiFetch({
                path: 'webba-booking/v1/calculate-amounts',
                method: 'POST',
                data: formData,
            })

            // Explicitly type response as any for conversion
            const res: any = response
            const amounts = {
                ...res,
                total: parseFloat(res.total),
                subtotal: parseFloat(res.subtotal),
                tax: parseFloat(res.tax),
                discount: parseFloat(res.discount),
                items: Array.isArray(res.items)
                    ? res.items.map((item: any) => ({
                          ...item,
                          price: parseFloat(item.price),
                      }))
                    : [],
            }

            dispatch({
                type: 'SET_BOOKING_AMOUNTS',
                data: amounts,
            })
        }
    },
    updateBooking:
        () =>
        async ({ select, dispatch }: any) => {
            const response: any = await apiFetch({
                path: `/wbk/v2/update-booking`,
                method: 'POST',
                data: select.getFormData(),
            })
            let bookings = select.getUserFutureBookings()
            const index = bookings.findIndex((booking: Booking) => {
                return Number(booking.id) === Number(response.booking.id)
            })
            if (index !== -1) {
                bookings[index] = { ...bookings[index], ...response.booking }
            }
            dispatch.setUserFutureBookings(bookings)
        },
    deleteBooking:
        () =>
        async ({ select, dispatch }: any) => {
            await apiFetch({
                path: `/wbk/v2/delete-booking`,
                method: 'POST',
                data: select.getFormData(),
            })
            let bookings = select.getUserFutureBookings()
            const updatedBookings = bookings.filter((booking: Booking) => {
                return booking.id !== select.getFormData().booking
            })
            dispatch.setUserFutureBookings(updatedBookings)
        },
    placeBooking:
        (formData: IFormData) =>
        async ({ select, dispatch }: any) => {
            dispatch.setLoading('createBooking', true)
            try {
                const formDataModified = new FormData()

                Object.keys(formData).forEach((key) => {
                    const value = formData[key]

                    if (key === 'attachments') {
                        value?.file.forEach((file) => {
                            formDataModified.append('attachments[]', file)
                        })
                        return
                    }

                    // formDataModified
                    if (typeof value === 'object') {
                        formDataModified.append(key, JSON.stringify(value))
                    } else {
                        formDataModified.append(key, value as any)
                    }
                })

                // formDataModified.append('params', JSON.stringify(formData))

                const response: any = await apiFetch({
                    path: `/webba-booking/v1/create-booking`,
                    method: 'POST',
                    // data: formData,
                    // body: formDataModified,
                    body: formDataModified,
                })

                dispatch({
                    type: 'UPDATE_BOOKING_DATA',
                    data: response,
                })

                return response
            } finally {
                dispatch.setLoading('createBooking', false)
            }
        },
    submitStripePayment:
        (payment_intent_id: string, payment_method_id: string) => async () => {
            const response = await apiFetch({
                path: '/webba-booking/v1/execute-stripe-payment',
                method: 'POST',
                data: { payment_intent_id, payment_method_id },
            })
            return response
        },
    initializePaymentMethod:
        (token: string, payment_method: string) => async () => {
            const response = await apiFetch({
                path: '/webba-booking/v1/initialize-payment-method',
                method: 'POST',
                data: { token, payment_method },
            })
            return response
        },
    fetchBookingDetails: (
        token: string,
        token_type: 'customer_token' | 'admin_token'
    ) => {
        return async ({ dispatch }: any) => {
            const response = await apiFetch({
                path: addQueryArgs(
                    `/webba-booking/v1/get-booking-ids-by-token`,
                    {
                        token,
                        token_type,
                    }
                ),
            })
            dispatch({
                type: 'SET_BOOKING_DETAILS',
                token,
                token_type,
                data: response,
            })
            return response
        }
    },
}

const store: StoreDescriptor = createReduxStore(
    'webba_booking/frontend_store',
    {
        reducer(state: State = DEFAULT_STATE, action: any): State {
            switch (action.type) {
                case 'SET_USER_FUTURE_BOOKING':
                    return { ...state, userFutureBookings: action.bookings }
                case 'SET_USER_PAST_BOOKING':
                    return { ...state, userPastBookings: action.bookings }
                case 'SET_PRESET':
                    return { ...state, preset: action.preset }
                case 'SET_PRESET_LOADING':
                    return {
                        ...state,
                        loading: { ...state.loading, preset: action.loading },
                    }
                case 'SET_USER_NAME':
                    return {
                        ...state,
                        preset: { ...state.preset, user: action.userName },
                    }
                case 'SET_FORM_DATA':
                    return {
                        ...state,
                        formData: {
                            ...state.formData,
                            [action.key]: action.value,
                        },
                    }
                case 'SET_DYNAMIC_ATTRIBUTE':
                    return {
                        ...state,
                        dynamicAttributes: {
                            ...state.dynamicAttributes,
                            [action.key]: action.value,
                        },
                    }
                case 'SET_SERVICE_TIMESLOTS':
                    return {
                        ...state,
                        serviceTimeslots: {
                            ...state.serviceTimeslots,
                            [action.serviceId]: {
                                ...state.serviceTimeslots[action.serviceId],
                                [action.date]: action.timeslots,
                            },
                        },
                    }
                case 'SET_SERVICE_AVAILABILITY':
                    return {
                        ...state,
                        serviceUnavailableDates: {
                            ...state.serviceUnavailableDates,
                            [action.data.service_id]: {
                                ...(state.serviceUnavailableDates[
                                    action.data.service_id
                                ] || {}),
                                [action.data.start_date]: action.data.dates,
                            },
                        },
                    }
                case 'SET_BOOKING_FORM_FIELDS':
                    return {
                        ...state,
                        bookingFormFields: action.data?.data?.form_fields || [],
                    }
                case 'SET_PAYMENT_METHODS':
                    return {
                        ...state,
                        paymentMethods:
                            action.data?.data?.payment_methods || [],
                    }
                case 'UPDATE_BOOKING_DATA':
                    return {
                        ...state,
                        bookingData: action.data,
                    }
                case 'SET_BOOKING_AMOUNTS':
                    return {
                        ...state,
                        bookingAmounts: action.data,
                    }
                case 'SET_LOADING': {
                    if (action.subkey !== undefined) {
                        return {
                            ...state,
                            loading: {
                                ...state.loading,
                                [action.key]: {
                                    ...((state.loading[
                                        action.key as keyof State['loading']
                                    ] as any) || {}),
                                    [action.subkey]: action.value,
                                },
                            },
                        }
                    } else {
                        return {
                            ...state,
                            loading: {
                                ...state.loading,
                                [action.key]: action.value,
                            },
                        }
                    }
                }
                case 'SET_BOOKING_DETAILS':
                    return {
                        ...state,
                        bookingDetails: {
                            ...state.bookingDetails,
                            [`${action.token_type}:${action.token}`]:
                                action.data,
                        },
                    }
                default:
                    return state
            }
        },
        actions,
        selectors: {
            getUserFutureBookings(state: State) {
                return state.userFutureBookings
            },
            getUserPastBookings(state: State) {
                return state.userPastBookings
            },
            getPreset(state: State) {
                return state.preset
            },
            getFormData(state: State) {
                return state.formData
            },
            getDynamicAttributes(state: State) {
                return state.dynamicAttributes
            },
            getSelectedDate(state: State) {
                return state.formData.date
            },
            getNextStep(state: State) {},
            getServiceAvailability(
                state: State,
                service_id: number,
                start_date: string
            ) {
                return state.serviceUnavailableDates &&
                    state.serviceUnavailableDates[service_id] &&
                    state.serviceUnavailableDates[service_id][start_date]
                    ? state.serviceUnavailableDates[service_id][start_date]
                    : []
            },
            getServiceTimeslots(state: State, serviceId: number, date: string) {
                return state.serviceTimeslots &&
                    state.serviceTimeslots[serviceId] &&
                    state.serviceTimeslots[serviceId][date]
                    ? state.serviceTimeslots[serviceId][date]
                    : []
            },
            getBookingFields(state: State) {
                return state.bookingFormFields
            },
            getPaymentMethods(state: State) {
                return state.paymentMethods
            },
            getBookingData(state: State) {
                return state.bookingData
            },
            getBookingAmounts(state: State) {
                return state.bookingAmounts
            },
            getLoading(state: State) {
                return state.loading
            },
            getBookingDetails: (
                state: State,
                token: string,
                token_type: string
            ) => {
                return state.bookingDetails?.[`${token_type}:${token}`]
            },
        },
        resolvers: {
            getUserFutureBookings:
                () =>
                async ({ dispatch }: any) => {
                    const result: any = await apiFetch({
                        path: addLangToQueryArgs(`/wbk/v2/get-user-bookings/`),
                    })
                    dispatch.setUserFutureBookings(result.bookings)
                },
            getUserPastBookings:
                () =>
                async ({ dispatch }: any) => {
                    const result: any = await apiFetch({
                        path: addLangToQueryArgs(`/wbk/v2/get-user-bookings/`, {
                            pastBookings: 'true',
                        }),
                    })
                    dispatch.setUserPastBookings(result.bookings)
                },
            getPreset:
                () =>
                async ({ dispatch }: any) => {
                    dispatch.setPresetLoading(true)
                    try {
                        const result: any = await apiFetch({
                            path: addLangToQueryArgs(`/wbk/v2/get-preset/`),
                        })
                        dispatch.setPreset({
                            ...result,
                            wording: Object.keys(result.wording).reduce(
                                (obj: any, key: string) => {
                                    obj[key] = wbkDecodeString(
                                        result.wording[key]
                                    )
                                    return obj
                                },
                                {}
                            ),
                        })
                    } finally {
                        dispatch.setPresetLoading(false)
                    }
                },
            getServiceTimeslots:
                (serviceId: number, date: string, offset: number) =>
                async ({ dispatch }: any) => {
                    const response = (await apiFetch({
                        path: addLangToQueryArgs(
                            `/webba-booking/v1/get-service-time-slots/`,
                            {
                                date,
                                service_id: serviceId,
                                offset,
                            }
                        ),
                        method: 'GET',
                    })) as { time_slots: any }

                    dispatch({
                        type: 'SET_SERVICE_TIMESLOTS',
                        date,
                        serviceId,
                        offset,
                        timeslots: response.time_slots,
                    })

                    return response.time_slots
                },
            getServiceAvailability:
                (service_id: Number, start_date: string, end_date: string) =>
                async ({ dispatch }: any) => {
                    const response = (await apiFetch({
                        path: addLangToQueryArgs(
                            `webba-booking/v1/get-service-availability/`,
                            {
                                service_id,
                                start_date,
                                end_date,
                            }
                        ),
                        method: 'GET',
                    })) as { dates: any }

                    dispatch({
                        type: 'SET_SERVICE_AVAILABILITY',
                        data: response,
                    })

                    return response?.dates || []
                },
            getBookingFields: (services: number[]) => {
                return async ({ dispatch }: any) => {
                    if (services.length === 0) return

                    const response = (await apiFetch({
                        path: addLangToQueryArgs(
                            `webba-booking/v1/get-form-fields`,
                            {
                                ids: services,
                            }
                        ),
                    })) as { data: any }

                    dispatch({
                        type: 'SET_BOOKING_FORM_FIELDS',
                        data: response,
                    })

                    return response.data.form_fields
                }
            },
            getPaymentMethods: (services: number[]) => {
                return async ({ dispatch }: any) => {
                    if (services.length === 0) return

                    const response = (await apiFetch({
                        path: addLangToQueryArgs(
                            `webba-booking/v1/get-payment-methods`,
                            {
                                ids: services,
                            }
                        ),
                    })) as { data: any }

                    dispatch({
                        type: 'SET_PAYMENT_METHODS',
                        data: response,
                    })

                    return response.data.payment_methods
                }
            },
            getBookingDetails: (
                token: string,
                token_type: 'customer_token' | 'admin_token'
            ) => {
                return async ({ dispatch }: any) => {
                    const response = await apiFetch({
                        path: addQueryArgs(
                            `/webba-booking/v1/get-booking-ids-by-token`,
                            {
                                token,
                                token_type,
                            }
                        ),
                    })

                    dispatch({
                        type: 'SET_BOOKING_DETAILS',
                        data: response,
                    })
                }
            },
        },
    }
)

register(store)

export const store_name = 'webba_booking/frontend_store'
export const default_state = DEFAULT_STATE
export type { State }
